/** PEOPLE ROLLUP — aggregated view of everyone mentioned in Fieldy debriefs.
 * Demo/presenting mode: fully masked — no names/data in the DOM. */
import React, { useEffect, useState, useCallback } from 'react';
import { TXT, SUB, TEAL, AMBER, MAGENTA, VIOLET, BORDER, HEAD, mini } from './V2DeskTiles';

const CARD = { border: `1px solid ${BORDER}`, borderRadius: 12, padding: '10px 12px', background: 'rgba(255,255,255,0.02)' };
const bodyP = { margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 12.5, lineHeight: 1.6, color: TXT };
const secH = { margin: '16px 0 8px', fontFamily: HEAD, fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: SUB };
const chip = (c) => ({ fontFamily: 'Source Sans 3, sans-serif', fontSize: 9.5, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: c, border: `1px solid ${c}55`, borderRadius: 999, padding: '2px 8px' });

const fmtD = (s) => new Date(s + 'T12:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });

export const pidOf = (n) => (n || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);

export const PeopleTab = ({ ctx, onJumpDay, openPid, focusPid }) => {
  const [q, setQ] = useState('');
  const [people, setPeople] = useState(null);
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mergeMode, setMergeMode] = useState(false);
  const [splitSel, setSplitSel] = useState(null);

  const loadIndex = useCallback((query = '') =>
    ctx.req('GET', `/api/admin/desk/people${query ? `?q=${encodeURIComponent(query)}` : ''}`)
      .then((x) => setPeople(x.people || [])).catch(() => setPeople([])), [ctx.req]);
  useEffect(() => { loadIndex(); }, [loadIndex]);

  const open = useCallback(async (pid) => {
    setLoading(true); setMergeMode(false); setSplitSel(null);
    try { setSel(await ctx.req('GET', `/api/admin/desk/people/${pid}`)); }
    catch { ctx.showToast('Could not load person'); setSel(null); }
    setLoading(false);
  }, [ctx]);
  useEffect(() => { if (openPid) open(openPid); }, [openPid, open]);

  if (ctx.demo) return null; // DOM-masked while presenting

  const search = (v) => { setQ(v); loadIndex(v); };
  const act = async (fn, okMsg) => {
    try { const r = await fn(); ctx.showToast(typeof okMsg === 'function' ? okMsg(r) : okMsg); return r; }
    catch (e) { ctx.showToast('Failed — ' + (String(e?.message || '').includes('404') ? 'no matching client' : 'try again')); }
  };
  const addLead = () => act(() => ctx.req('POST', '/api/admin/desk/debrief/person_add',
    { name: sel.name, context: sel.summary || '', as: 'lead' }), `✅ ${sel.name} added as lead`);
  const logClient = () => act(() => ctx.req('POST', '/api/admin/desk/debrief/person_add',
    { name: sel.name, context: sel.summary || '', as: 'client_note' }), (r) => `✅ Logged to ${r.logged_to}`);
  const addTask = () => {
    const t = window.prompt(`Task about ${sel.name}:`, `Follow up with ${sel.name}`);
    if (t) act(() => ctx.req('POST', `/api/admin/desk/people/${sel.person_id}/task_add`, { title: t }),
      '✅ Task added to Desk').then(() => ctx.load && ctx.load());
  };
  const rename = () => {
    const n = window.prompt('Rename this person:', sel.name);
    if (n && n.trim()) act(() => ctx.req('POST', `/api/admin/desk/people/${sel.person_id}/rename`, { name: n.trim() }),
      '✓ Renamed').then(() => { open(sel.person_id); loadIndex(q); });
  };
  const doMerge = (dst) => act(() => ctx.req('POST', '/api/admin/desk/people/merge',
    { from_id: sel.person_id, into_id: dst.person_id }), `✓ Merged into ${dst.name}`)
    .then(() => { loadIndex(q); open(dst.person_id); });
  const doSplit = () => {
    const dates = [...splitSel];
    act(() => ctx.req('POST', `/api/admin/desk/people/${sel.person_id}/split`, { dates }),
      '✂ Split — new person created').then(() => { loadIndex(q); open(sel.person_id); });
  };
  const linkLead = () => act(() => ctx.req('POST', `/api/admin/desk/people/${sel.person_id}/link_lead`,
    { lead_id: sel.lead_suggestion.lead_id }), '🔗 Linked to client profile').then(() => open(sel.person_id));

  // ── person rollup panel ──
  if (loading) return <p style={{ ...bodyP, marginTop: 16, color: SUB }}>Building rollup…</p>;
  if (sel) {
    const groups = { 'still open': [], stale: [], kept: [], dismissed: [] };
    (sel.promises || []).forEach((p) => (groups[p.status] = groups[p.status] || []).push(p));
    const statusC = { 'still open': AMBER, stale: MAGENTA, kept: TEAL, dismissed: SUB };
    return (
      <div data-testid="people-rollup">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
          <button data-testid="people-back" onClick={() => setSel(null)} style={mini}>← all people</button>
          <h3 data-testid="people-rollup-name" style={{ margin: 0, fontFamily: HEAD, fontSize: 16, fontWeight: 800, color: TXT, flex: 1 }}>{sel.name}</h3>
          <span style={{ ...bodyP, fontSize: 10.5, color: SUB }}>{sel.first_seen === sel.last_seen ? fmtD(sel.first_seen) : `${fmtD(sel.first_seen)} → ${fmtD(sel.last_seen)}`}</span>
        </div>

        {sel.linked_lead && (
          <p data-testid="people-linked-lead" style={{ ...bodyP, marginTop: 8, fontSize: 11.5 }}>
            🔗 Linked client: <a href={`/admin/leads?open=${sel.linked_lead.lead_id}`} style={{ color: TEAL, fontWeight: 700 }}>{sel.linked_lead.business_name || sel.linked_lead.first_name}</a>
            {sel.linked_lead.stage ? <span style={{ color: SUB }}> · {sel.linked_lead.stage}</span> : null}
          </p>
        )}
        {!sel.linked_lead && sel.lead_suggestion && (
          <div data-testid="people-lead-suggestion" style={{ ...CARD, marginTop: 10, borderColor: `${TEAL}55`, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <p style={{ ...bodyP, fontSize: 11.5, flex: 1, minWidth: 160 }}>Looks like existing client <b>{sel.lead_suggestion.label}</b> — link them?</p>
            <button data-testid="people-link-confirm" onClick={linkLead} style={{ ...mini, color: '#0E0F11', background: TEAL, border: 'none', fontWeight: 800 }}>🔗 Link</button>
          </div>
        )}

        <p style={secH}>Relationship</p>
        <p data-testid="people-summary" style={bodyP}>{sel.summary || '—'}</p>

        <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
          <button data-testid="people-add-lead" onClick={addLead} style={{ ...mini, color: TEAL }}>+ add as lead</button>
          <button data-testid="people-log-client" onClick={logClient} style={{ ...mini, color: VIOLET }}>✎ log to client</button>
          <button data-testid="people-add-task" onClick={addTask} style={{ ...mini, color: AMBER }}>＋ add task</button>
          <span style={{ flex: 1 }} />
          <button data-testid="people-rename" onClick={rename} style={{ ...mini, color: SUB }}>✎ rename</button>
          <button data-testid="people-merge" onClick={() => { setMergeMode(!mergeMode); setSplitSel(null); }} style={{ ...mini, color: mergeMode ? TEAL : SUB }}>⇄ merge</button>
          <button data-testid="people-split" onClick={() => { setSplitSel(splitSel ? null : new Set()); setMergeMode(false); }} style={{ ...mini, color: splitSel ? TEAL : SUB }}>✂ split</button>
        </div>

        {mergeMode && (
          <div data-testid="people-merge-picker" style={{ ...CARD, marginTop: 10 }}>
            <p style={{ ...bodyP, fontSize: 11, color: SUB, marginBottom: 6 }}>Merge <b>{sel.name}</b> into…</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {(people || []).filter((p) => p.person_id !== sel.person_id).map((p) => (
                <button key={p.person_id} data-testid={`people-merge-into-${p.person_id}`} onClick={() => doMerge(p)} style={{ ...mini, fontSize: 10.5 }}>{p.name}</button>
              ))}
            </div>
          </div>
        )}
        {splitSel && (
          <div style={{ ...CARD, marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <p style={{ ...bodyP, fontSize: 11, color: SUB, flex: 1, minWidth: 160 }}>Tick the days below that belong to a <b>different</b> {sel.name}, then confirm.</p>
            <button data-testid="people-split-confirm" onClick={doSplit} disabled={!splitSel.size}
              style={{ ...mini, color: '#0E0F11', background: splitSel.size ? AMBER : BORDER, border: 'none', fontWeight: 800 }}>✂ split {splitSel.size || ''}</button>
          </div>
        )}

        {(sel.promises || []).length > 0 && (
          <>
            <p style={secH}>⚡ Promises</p>
            {['still open', 'stale', 'kept', 'dismissed'].map((st) => (groups[st] || []).map((p, i) => (
              <div key={st + i} data-testid={`people-promise-${st.replace(' ', '-')}-${i}`}
                style={{ ...CARD, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                         ...(focusPid && p.promise_id === focusPid ? { borderColor: AMBER, boxShadow: `0 0 14px ${AMBER}33` } : {}) }}>
                <div style={{ flex: 1, minWidth: 170 }}>
                  <p style={{ ...bodyP, fontWeight: 700 }}>{p.what}</p>
                  <p style={{ ...bodyP, fontSize: 11, color: SUB }}>{fmtD(p.date)}{p.deadline_date ? ` · due ${p.deadline_date}` : ''}</p>
                </div>
                <span style={chip(statusC[st])}>{st}</span>
              </div>
            )))}
          </>
        )}

        <p style={secH}>Timeline</p>
        {(sel.mentions || []).map((m, i) => (
          <div key={i} data-testid={`people-mention-${i}`} style={{ ...CARD, marginBottom: 7, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            {splitSel && (
              <input type="checkbox" data-testid={`people-split-check-${m.date}`} checked={splitSel.has(m.date)}
                onChange={() => { const s = new Set(splitSel); s.has(m.date) ? s.delete(m.date) : s.add(m.date); setSplitSel(s); }}
                style={{ marginTop: 3, accentColor: AMBER }} />
            )}
            <button data-testid={`people-jump-${m.date}`} onClick={() => onJumpDay(m.date)}
              style={{ ...mini, fontSize: 10, color: TEAL, whiteSpace: 'nowrap' }}>{fmtD(m.date)} →</button>
            <p style={{ ...bodyP, fontSize: 11.5, flex: 1 }}>{m.context}</p>
          </div>
        ))}
      </div>
    );
  }

  // ── index ──
  return (
    <div data-testid="people-index">
      <input data-testid="people-search" value={q} onChange={(e) => search(e.target.value)} placeholder="Search people…"
        style={{ width: '100%', boxSizing: 'border-box', marginTop: 14, padding: '9px 12px', borderRadius: 10, border: `1px solid ${BORDER}`, background: 'rgba(255,255,255,0.03)', color: TXT, fontFamily: 'Source Sans 3, sans-serif', fontSize: 12.5, outline: 'none' }} />
      {people === null && <p style={{ ...bodyP, marginTop: 14, color: SUB }}>…</p>}
      {people && people.length === 0 && <p style={{ ...bodyP, marginTop: 14, color: SUB }}>No people yet — rollups build from your debriefs.</p>}
      <div style={{ marginTop: 12 }}>
        {(people || []).map((p) => (
          <div key={p.person_id} data-testid={`people-row-${p.person_id}`} onClick={() => open(p.person_id)}
            style={{ ...CARD, marginBottom: 7, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 150 }}>
              <p style={{ ...bodyP, fontWeight: 700 }}>{p.name}</p>
              <p style={{ ...bodyP, fontSize: 10.5, color: SUB }}>{p.mention_count} mention{p.mention_count === 1 ? '' : 's'} · last {fmtD(p.last_seen)}</p>
            </div>
            {p.open_promises > 0 && <span style={chip(AMBER)}>⚡ {p.open_promises} open</span>}
            {p.linked_lead_id && <span style={chip(TEAL)}>🔗 client</span>}
            {!p.linked_lead_id && p.lead_suggestion && <span style={chip(VIOLET)}>match?</span>}
          </div>
        ))}
      </div>
    </div>
  );
};
